import { useNavigate } from "react-router-dom";
import { COMPONENTS_LessonCom, Sounds } from "../../assets/asset";
import { Player } from "@lottiefiles/react-lottie-player";
import animationData from "../../utils/happy_dog_animation.json";
import MCQComponent from "../QuestionTypes/MCQComponent";
import SelectNumComponent from "../QuestionTypes/SelectNumComponent";
import ImageMCQComponent from "../QuestionTypes/ImageMCQComponent";
import Trace from "../QuestionTypes/Trace";
import { useState, useEffect } from "react";
import Txt from "../QuestionTypes/Txt";
import MultipleSelectImg from "../QuestionTypes/MultipleSelectImg";
import Checkboxtype from "../QuestionTypes/Checkboxtype";
import VerticalImageMcq from "../QuestionTypes/VerticalImageMCQ";

const TraceCom = ({ lessonData, yearId }) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [setFeedbackMessage] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [setArrangementResult] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [userInput, setUserInput] = useState("");

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };


  const questionData = lessonData.data[currentQuestionIndex]?.fields || {};
  const { question, questionType, answer, questionaudio } = questionData;

  const correctSound = new Audio(Sounds.correct);
  const incorrectSound = new Audio(Sounds.incorrect);

  useEffect(() => {
    if (questionaudio) {
      const audio = new Audio(questionaudio);
      audio.play().catch((err) => console.error("Failed to play audio:", err));
    }
  }, [questionaudio]);

  const handleOptionClick = (index) => {
    setIsOptionSelected(true);
    setSelectedIndex(index);
  };

  const handleArrangementClick = (result) => {
    setArrangementResult(result);
    setIsOptionSelected(true);
  };

  const handleAnswerChange = (selectedType, userInput) => {
    setUserInput(userInput);
    handleTypeChange(selectedType);
  };

  const handleContinue = () => {
    setHasAttempted(true);
    setFeedbackMessage("");
    if (
      [
        "MCQ",
        "SelectNum",
        "ImageMCQ",
        "VerticalImageMcq",
        "QuestionImage",
        "MultipleSelectImg",
        "Checkboxtype",
      ].includes(questionType)
    ) {
      if (selectedIndex !== null) {
        if (Number(selectedIndex) === Number(answer)) {
          setIsAnswerCorrect(true);
          setFeedbackMessage("Correct answer!");
          playCorrectSoundAndContinue();
        } else {
          setIsAnswerCorrect(false);
          incorrectSound
            .play()
            .catch((err) =>
              console.error("Failed to play incorrect sound:", err)
            );
          setFeedbackMessage("Incorrect answer. Please try again.");
        }
      } else if (questionType === "Checkboxtype") {
        const isCorrectAnswer =
          (userInput + selectedType).toUpperCase() === answer[0];

        if (isCorrectAnswer) {
          setIsAnswerCorrect(true);
          setFeedbackMessage("Correct answer!");
          playCorrectSoundAndContinue();
        } else {
          setIsAnswerCorrect(false);
          incorrectSound
            .play()
            .catch((err) =>
              console.error("Failed to play incorrect sound:", err)
            );
          setFeedbackMessage("Incorrect answer. Please try again.");
        }
      }
    } else {
      moveToNextQuestion();
    }
  };

  const playCorrectSoundAndContinue = () => {
    setIsPlayingAudio(true);

    correctSound
      .play()
      .then(() => {
        correctSound.onended = () => {
          moveToNextQuestion();
          setIsPlayingAudio(false);
          setFeedbackMessage("");
        };
      })
      .catch((err) => {
        console.error("Failed to play correct sound:", err);
        setIsPlayingAudio(false);
      });
  };

  const moveToNextQuestion = () => {
    if (currentQuestionIndex < lessonData.data.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setIsOptionSelected(false);
      setSelectedIndex(null);
      setIsAnswerCorrect(null);
      setFeedbackMessage("");
      setHasAttempted(false);
    } else {
      navigate("/lessonDashboard", { state: { yearId } });
    }
  };

  const progressPercentage =
    (currentQuestionIndex / lessonData.data.length) * 100;

  return (
    <div className="flex flex-col w-screen h-screen items-center justify-between">
      <div className="w-full bg-[#00000099] p-4 flex items-center">
        <img
          src={COMPONENTS_LessonCom.back}
          alt="Back"
          className="cursor-pointer w-8 h-8"
          onClick={() => navigate(-1)}
        />
        <div className="flex-1 mx-4">
          <div className="bg-gray-200 h-2 rounded mx-40">
            <div
              className="bg-blue-600 h-full transition-all duration-300 rounded-l"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
        <div className="text-white">
          {currentQuestionIndex + 1} / {lessonData.data.length}
        </div>
      </div>

      <div className="flex w-[80%] h-[80%] flex-col items-center justify-center font-mali">
        <div className="bg-white bg-opacity-75 flex-[1] w-[80%] m-0 rounded-t-xl flex items-center relative shadow-lg">
          <div className="text-4xl m-0 text-black font-bold opacity-90">
            {question}
          </div>
        </div>

        <div className="bg-white bg-opacity-50 flex-[5] w-[80%] flex items-center justify-center">
          {questionType === "MCQ" && (
            <MCQComponent
              questionData={questionData}
              onOptionClick={handleOptionClick}
            />
          )}
          {questionType === "SelectNum" && (
            <SelectNumComponent
              questionData={questionData}
              onOptionClick={handleOptionClick}
            />
          )}
          {questionType === "ImageMCQ" && (
            <ImageMCQComponent
              questionData={questionData}
              onOptionClick={handleOptionClick}
            />
          )}
          {questionType === "VerticalImageMcq" && (
            <VerticalImageMcq
              questionData={questionData}
              onOptionClick={handleOptionClick}
            />
          )}
          {questionType === "DrawText" && (
            <Trace
              questionData={questionData}
              onOptionClick={handleOptionClick}
            />
          )}
          {questionType === "Txt" && (
            <Txt
              questionData={questionData}
              onOptionClick={handleArrangementClick}
            />
          )}
          {questionType === "MultipleSelectImg" && (
            <MultipleSelectImg
              questionData={questionData}
              onOptionClick={handleArrangementClick}
            />
          )}
          {questionType === "Checkboxtype" && (
            <Checkboxtype
              questionData={questionData}
              onAnswerChange={handleAnswerChange}
            />
          )}
        </div>

        <div className="bg-white bg-opacity-75 flex-[1] w-[80%] rounded-b-xl flex items-center justify-between pr-7 shadow-lg mb-10">
          <div className="flex gap-[5px] items-center">
            <Player
              autoplay
              loop
              src={animationData}
              style={{ height: "150px", width: "150px" }}
            />
            {hasAttempted && isAnswerCorrect === false && (
              <div className="border-4 border-red-500 rounded-lg bg-red-100 p-[5px] text-base text-red-500 px-[20px] text-center h-min">
                Oops, that's incorrect
              </div>
            )}
            {hasAttempted && isAnswerCorrect === true && (
              <div className="border-4 border-green-500 rounded-lg bg-green-100 p-[5px] text-base text-black px-[20px] text-center h-min">
                That's correct
              </div>
            )}
          </div>
          <button
            className={`${
              [
                "MCQ",
                "SelectNum",
                "ImageMCQ",
                "VerticalImageMcq",
                "Txt",
                "DrawText",
                "MultipleSelectImg",
                "Checkboxtype",
              ].includes(questionType) && !isOptionSelected
                ? "bg-gray-400"
                : isAnswerCorrect === false
                ? "bg-red-500"
                : hasAttempted && isAnswerCorrect === true
                ? "bg-green-500 hover:bg-green-600"
                : "bg-gray-500"
            } text-white font-semibold py-2 px-6 rounded-full flex items-center opacity-80 shadow-md`}
            onClick={handleContinue}
            disabled={
              isPlayingAudio ||
              ([
                "MCQ",
                "SelectNum",
                "ImageMCQ",
                "Txt",
                "VerticalImageMcq",
                "Checkboxtype",
              ].includes(questionType) &&
                !isOptionSelected)
            }
          >
            <span>Click to continue</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 ml-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TraceCom;
